import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, DragEvent, MouseEvent as ReactMouseEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Input from '../../form/Input';
import BigButton from '../../button/BigButton';
import FileUpload from '../../form/FileUpload';
import apiClient from '../../../service/apiClient';
import {
  useFortuneLabCodeStore,
  IFortuneLabCode,
  IFortuneLabCodeCreateRequest,
} from '../../../store/useFortuneLabCode';
import { useFranchiseStore } from '../../../store/useFranchise';
import { getFranchiseMailOrderNumber } from '../../../util/franchise';
import LandingBusinessFooter from '../common/LandingBusinessFooter';
import './CodeModal.css';

interface CodeModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  code?: IFortuneLabCode | null;
  onClose: () => void;
  onSuccess?: () => Promise<void> | void;
}

interface FormState {
  campaignName: string;
  couponName: string;
  linkSlug: string;
  landingImageUrls: string[];
  sendYn: boolean;
}

const getTomorrowExpiryLabel = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const extractFileName = (assetUrl: string) => {
  try {
    const decoded = decodeURIComponent(assetUrl);
    const lastSegment = decoded.split('?')[0]?.split('/').pop();
    return lastSegment && lastSegment.trim().length > 0 ? lastSegment : decoded;
  } catch (_) {
    const fallback = assetUrl.split('?')[0];
    const lastSegment = fallback.split('/').pop();
    return lastSegment && lastSegment.trim().length > 0 ? lastSegment : assetUrl;
  }
};

const normalizeSlug = (slug: string) => {
  const trimmed = slug?.trim() ?? '';
  if (!trimmed) {
    return '';
  }
  return trimmed.replace(/^(https?:\/\/)/i, '').replace(/^\/\//, '');
};

const buildInitialForm = (code: IFortuneLabCode | null | undefined): FormState => {
  const fallbackAsset = (code as unknown as { landingGifUrl?: string } | undefined)?.landingGifUrl;
  const assetsFromCode = (code as IFortuneLabCode | undefined)?.landingImageUrls ?? (fallbackAsset ? [fallbackAsset] : []);
  return {
    campaignName: code?.campaignName ?? '',
    couponName: code?.couponName ?? '',
    linkSlug: normalizeSlug(code?.linkSlug ?? ''),
    landingImageUrls: assetsFromCode,
    sendYn: Boolean(code?.sendYn),
  };
};

const CodeModal = ({ isOpen, mode, code, onClose, onSuccess }: CodeModalProps) => {
  const { createCode, codeList } = useFortuneLabCodeStore();
  const myfranchise = useFranchiseStore(state => state.myfranchise);
  const getMyFranchise = useFranchiseStore(state => state.getMyFranchise);
  const [form, setForm] = useState<FormState>(() => buildInitialForm(code));
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const resetState = useCallback(() => {
    setForm(buildInitialForm(code));
    setPendingFiles([]);
    setUploading(false);
    setSaving(false);
    setDragIndex(null);
  }, [code]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    getMyFranchise();
  }, [getMyFranchise, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetState();
  }, [code, isOpen, resetState]);

  const hasBizProfileKey = Boolean(myfranchise?.bizmProfileKey);

  useEffect(() => {
    if (!hasBizProfileKey) {
      setForm(prev => (prev.sendYn ? { ...prev, sendYn: false } : prev));
    }
  }, [hasBizProfileKey]);

  useEffect(() => {
    if (pendingFiles.length === 0) {
      return;
    }

    let isCancelled = false;

    const uploadAssets = async () => {
      setUploading(true);
      const uploadedUrls: string[] = [];

      try {
        for (const file of pendingFiles) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await apiClient.post('/upload-file/upload', formData);
          const fileUrl = response.data?.fileFullUrl ?? '';
          if (fileUrl) {
            uploadedUrls.push(fileUrl);
          }
        }

        if (!isCancelled && uploadedUrls.length > 0) {
          setForm(prev => ({
            ...prev,
            landingImageUrls: [...prev.landingImageUrls, ...uploadedUrls],
          }));
        }
      } catch (error) {
        console.error('랜딩 이미지 업로드 실패', error);
        if (!isCancelled) {
          toast.error('랜딩 이미지 업로드에 실패했습니다.');
        }
      } finally {
        if (!isCancelled) {
          setUploading(false);
          setPendingFiles([]);
        }
      }
    };

    uploadAssets();

    return () => {
      isCancelled = true;
    };
  }, [pendingFiles]);

  const handleInputChange = (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleRemoveAsset = useCallback((index: number) => (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setForm(prev => ({
      ...prev,
      landingImageUrls: prev.landingImageUrls.filter((_, assetIndex) => assetIndex !== index),
    }));
  }, []);

  const handleDragStart = useCallback((index: number) => (event: DragEvent<HTMLDivElement>) => {
    setDragIndex(index);
    event.dataTransfer.setData('text/plain', String(index));
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((index: number) => (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      return;
    }

    setForm(prev => {
      const nextAssets = [...prev.landingImageUrls];
      const [moved] = nextAssets.splice(dragIndex, 1);
      nextAssets.splice(index, 0, moved);
      return {
        ...prev,
        landingImageUrls: nextAssets,
      };
    });
    setDragIndex(null);
  }, [dragIndex]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
  }, []);

  const handleSendToggle = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setForm(prev => ({ ...prev, sendYn: checked }));
  }, []);

  const handleSubmit = useCallback(async (event?: ReactMouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    if (saving || uploading) {
      return;
    }

    if (!form.campaignName.trim()) {
      toast.error('캠페인명을 입력해주세요.');
      return;
    }
    if (form.sendYn) {
      if (!form.couponName.trim()) {
        toast.error('쿠폰명을 입력해주세요.');
        return;
      }
      if (!form.linkSlug.trim()) {
        toast.error('쿠폰 링크를 입력해주세요.');
        return;
      }
    }
    const trimmedCouponName = form.couponName.trim();
    const normalizedSlug = normalizeSlug(form.linkSlug);
    const landingImageUrls = form.landingImageUrls.map(asset => asset.trim()).filter(Boolean);

    const payload: IFortuneLabCodeCreateRequest = {
      campaignName: form.campaignName.trim(),
      couponName: trimmedCouponName || undefined,
      linkSlug: normalizedSlug || undefined,
      landingImageUrls: landingImageUrls.length > 0 ? landingImageUrls : undefined,
      sendYn: form.sendYn,
    };

    setSaving(true);
    try {
      if (mode === 'create') {
        await createCode(payload);
        handleClose();
      } else if (code) {
        let success = false;
        try {
          await apiClient.patch(`/fortune-lab/tracking/${code.id}`, payload);
          toast.success('캠페인 정보가 수정되었습니다.');
          if (onSuccess) {
            await onSuccess();
          }
          success = true;
        } catch (error) {
          console.error('fortune-lab campaign update error', error);
          toast.error('캠페인 수정에 실패했습니다.');
        } finally {
          if (success) {
            handleClose();
          }
        }
      }
    } finally {
      setSaving(false);
    }
  }, [code, createCode, form, handleClose, mode, onSuccess, saving, uploading]);

  const expiryPreview = getTomorrowExpiryLabel();
  const previewReceiver = '고객';
  const previewCouponName = form.couponName.trim() || '특별 쿠폰';
  const normalizedLink = form.linkSlug.trim() ? normalizeSlug(form.linkSlug) : '';
  const previewLink = normalizedLink ? `https://${normalizedLink}` : '';
  const previewSender =
    (mode === 'edit'
      ? (code?.bizProfileName?.trim() || code?.companyName?.trim())
      : undefined) ||
    codeList?.[0]?.bizProfileName?.trim() ||
    codeList?.[0]?.companyName?.trim() ||
    '사주톡톡';
  const previewCompany = previewSender;
  const shouldSendCoupon = form.sendYn && hasBizProfileKey;
  const landingImageUrls = form.landingImageUrls;
  const hasLandingImages = landingImageUrls.length > 0;
  const handlePreviewLinkClick = useCallback(() => {
    if (!previewLink) {
      return;
    }
    window.open(previewLink, '_blank', 'noopener');
  }, [previewLink]);

  if (!isOpen) {
    return null;
  }

  const heading = mode === 'create' ? '캠페인 코드 생성' : '캠페인 코드 수정';
  const submitLabel = mode === 'create' ? (saving ? '생성 중...' : '생성') : (saving ? '수정 중...' : '수정');

  return (
    <div className="fortunelab_modal_overlay" onClick={handleClose}>
      <div
        className="fortunelab_modal fortunelab_modal--wide"
        onClick={event => event.stopPropagation()}
      >
        <div className="fortunelab_modal_header">
          <h2>{heading}</h2>
          <button className="fortunelab_modal_close" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="fortunelab_modal_body fortunelab_modal_body--grid">
          <div className="form-section">
            <div className="form-field">
              <label>캠페인명 *</label>
              <Input
                value={form.campaignName}
                onChange={handleInputChange('campaignName')}
                placeholder="캠페인명을 입력하세요"
                width="100%"
                textAlign="left"
                disabled={saving}
              />
            </div>

            <div className="form-field form-field--checkbox">
              <label className="send-checkbox">
                <input
                  type="checkbox"
                  checked={form.sendYn}
                  onChange={handleSendToggle}
                  disabled={saving || !hasBizProfileKey}
                />
                쿠폰 알림톡 보내기
              </label>
              {!hasBizProfileKey && (
                <p className="send-checkbox__notice">카카오 채널 먼저 연동 해주세요.</p>
              )}
            </div>

            <div className={`coupon-fields ${form.sendYn ? 'coupon-fields--visible' : 'coupon-fields--hidden'}`}>
              <div className="form-field">
                <label>쿠폰명</label>
                <Input
                  value={form.couponName}
                  onChange={handleInputChange('couponName')}
                  placeholder="쿠폰명을 입력하세요"
                  width="100%"
                  textAlign="left"
                  disabled={saving || !form.sendYn}
                />
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>유효기간 (자동)</label>
                  <div className="form-static form-static-muted">D+1일로 자동 세팅됩니다.</div>
                </div>
                <div className="form-field">
                  <label>쿠폰 링크</label>
                  <div className="fortunelab_modal_link_input">
                    <span className="link-prefix">https://</span>
                    <input
                      type="text"
                      value={form.linkSlug}
                      onChange={handleInputChange('linkSlug')}
                      placeholder="example.link"
                      disabled={saving || !form.sendYn}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-field">
              <label>랜딩 이미지 (선택)</label>
              <FileUpload
                setFiles={setPendingFiles}
                accept="image/*"
                multiple
                note="이미지 또는 GIF, 최대 20MB"
              />
              {hasLandingImages && (
                <div className="landing-assets-list">
                  {landingImageUrls.map((asset, index) => (
                    <div
                      key={`${asset}-${index}`}
                      className="landing-asset-card"
                      draggable
                      onDragStart={handleDragStart(index)}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop(index)}
                      onDragEnd={handleDragEnd}
                    >
                      <button
                        type="button"
                        className="landing-asset-drag"
                        aria-label="순서 변경"
                        disabled={uploading || saving}
                      >
                        <FontAwesomeIcon icon={faGripVertical} />
                      </button>
                      <div className="landing-asset-info">
                        <span className="landing-asset-index">#{index + 1}</span>
                        <span className="landing-asset-name">{extractFileName(asset)}</span>
                      </div>
                      <button
                        type="button"
                        className="landing-asset-remove"
                        onClick={handleRemoveAsset(index)}
                        disabled={saving || uploading}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        제거
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="preview-column">
            <section className={`preview-panel ${shouldSendCoupon ? '' : 'preview-panel--disabled'}`}>
              <h3>쿠폰 알림톡 미리보기</h3>
              <div className="alimtalk-preview">
                <div className="alimtalk-header">{previewCompany}</div>
                <div className="alimtalk-body">
                  {shouldSendCoupon ? (
                    <>
                      <p>💡회원님을 위한 특별 쿠폰</p>
                      <p>📜{previewCouponName}이 곧 사라져요!</p>
                      <br />
                      <p>안녕하세요 {previewReceiver}님</p>
                      <p>{previewSender}입니다.</p>
                      <br />
                      <p>{previewReceiver}님을 위한</p>
                      <p>{previewCouponName}이 남아있어요.</p>
                      <p>바로 확인해보세요🎯</p>
                      <br />
                      <p>🔑할인 쿠폰 사용법</p>
                      <p>▶ 쿠폰명 : {previewCouponName}</p>
                      <p>▶ 유효기간 : {expiryPreview}</p>
                      <p>▶ 사용방법 : 아래 비밀 링크에서 신청 시 적용</p>
                    </>
                  ) : (
                    <p className="preview-disabled">쿠폰 알림톡 발송이 비활성화되어 있습니다.</p>
                  )}
                </div>
                <div className="alimtalk-footer">
                  <button
                    type="button"
                    className="button"
                    onClick={handlePreviewLinkClick}
                    disabled={!shouldSendCoupon || saving || uploading || !previewLink}
                  >
                    쿠폰 확인하기
                  </button>
                </div>
              </div>
            </section>

            <section className="preview-panel">
              <h3>랜딩 미리보기</h3>
              <div className="landing-preview">
                <div className="landing-preview__content">
                  <div className="landing-preview__media">
                    {hasLandingImages ? (
                      landingImageUrls.map((asset, index) => (
                        <img key={`${asset}-${index}`} src={asset} alt={`랜딩 이미지 ${index + 1}`} />
                      ))
                    ) : (
                      <div className="landing-preview__placeholder">
                        랜딩 이미지나 GIF를 업로드하면 이곳에 표시됩니다.<br />업로드하지 않으면 기본 디자인이 노출됩니다.
                      </div>
                    )}
                  </div>

                  <div className="button_description landing-preview__button-description">
                    <span className="highlight">무료운세 확인하고</span> 쿠폰받기
                  </div>

                  <form
                    className="fortune_lab_form landing-preview__form"
                    onSubmit={event => event.preventDefault()}
                  >
                    <input
                      type="text"
                      placeholder="이름"
                      autoComplete="name"
                      maxLength={20}
                      value=""
                      disabled
                    />
                    <input
                      type="tel"
                      placeholder="전화번호 (숫자만 입력)"
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={11}
                      value=""
                      disabled
                    />
                    <input
                      type="text"
                      placeholder="생년월일 (yyyy.mm.dd)"
                      autoComplete="bday"
                      inputMode="numeric"
                      value=""
                      disabled
                    />

                    <div className="privacy_agreement">
                      <label className="privacy_agreement_checkbox">
                        <input type="checkbox" disabled />
                        <span className="privacy_agreement_text">
                          개인정보 수집·이용에 동의합니다
                        </span>
                      </label>
                    </div>

                    <button type="button" className="kakao_button" disabled>
                      3초만에 운세 확인하기
                    </button>
                  </form>

                  <LandingBusinessFooter
                    companyNumber={myfranchise?.companyNumber}
                    mailOrderNumber={getFranchiseMailOrderNumber(myfranchise)}
                    companyName={myfranchise?.companyName}
                    ceo={myfranchise?.ceo}
                    serviceName={myfranchise?.bizProfileName}
                  />
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="fortunelab_modal_footer">
          <BigButton
            type="submit"
            text={submitLabel}
            onClick={handleSubmit}
            style={{ flex: '0 0 50%' }}
            disabled={saving || uploading}
          />
        </div>
      </div>
    </div>
  );
};

export default CodeModal;
